import { useLayoutEffect, useRef, useState, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { cubicBezier, useMotionValue, useMotionValueEvent, useScroll, useTransform, animate, AnimatePresence } from "framer-motion"
import styled from "@emotion/styled"
import { createPortal } from "react-dom"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"

import { buttonInline, green } from "css/buttons"
import { visuallyHidden } from "css/text"

import { cssVars } from "components/nav-v2/Wrapper"
import { useNav } from "components/nav-v2/Wrapper"
import { storeCustomEvent } from "components/analytics/DigitalDataLayer"
import Menu from "./Menu"
import Card from "./Card"
import {
  observeResize,
  setProperty,
  setAttribute,
  rafDelay,
  getSelected,
  setSelected,
  PARAM_KEY_CATEGORY,
  PARAM_KEY_FILTER,
  PARAM_KEY_RANGE,
  FILTER_SEPERATOR,
  COL_MAPPING,
  OPACITY_DURATION_OUT,
  OPACITY_DURATION_IN,
  RANGE_INC,
} from "./utils"
import { SGProvider, useSG } from "./context"
import { useAggregator } from "components/aggregator/Context"
import { aggregatorPathByTerm } from "components/event/Event"
import { useNavigation } from "contexts/Navigation"
import { useLocale } from "contexts/Locale"
import ModalContainer, { fallbackModal } from "components/modal/ModalContainer"
import useSSR from "hooks/useSSR"
import { useEnv } from "contexts/Env"
import Link from "components/link/Link"

const Main = styled.section`
  --maxw: 2560px;
  --maxwidth: min(100vw, var(--maxw));
  --sg-gap: calc(var(--maxwidth) * 0.0125);
  --sg-gap-margin: calc(var(--sg-gap) / 2);

  position: relative;
  display: grid;
  grid-template-columns: calc((100vw - var(--maxwidth)) / 2) 1fr calc((100vw - var(--maxwidth)) / 2);
  grid-template-rows: 100%;
  align-items: start;
  width: 100vw;
`

const Background = styled.div`
  position: sticky;
  top: 0;
  height: 100%;
  max-height: 100dvh;
  grid-row: 1/-1;
  grid-column: 2;
  background: var(--sg-background, transparent);
`

const Grid = styled.div`
  position: relative;
  grid-row: 1/-1;
  grid-column: 2;
  display: grid;
  grid-template-rows: 1fr;
  align-items: center;
  justify-items: center;
  box-sizing: border-box;
`

const Anchor = styled.div`
  position: absolute;
  --sg-anchor-top: calc(var(${cssVars.main.safe}) * var(${cssVars.main.bar.pinned}));
  top: calc(var(--sg-anchor-top) * -1 + 1px);
  grid-row: 1/-1;
  grid-column: 2;
`

const List = styled.ul`
  --sg-ratio-1: 800/1000;
  --sg-ratio-2: 800/1334;
  --sg-row-span-1: 20;
  --sg-row-span-2: 27;
  --sg-opacity-duration: calc(var(--sg-opacity-duration-in, 0) * var(--sg-opacity) + var(--sg-opacity-duration-out, 0) * calc(1 - var(--sg-opacity)));
  --sg-opacity-delay: calc(150 * var(--sg-opacity));
  --sg-card-width: calc((var(--maxwidth) - var(--sg-gap) * (var(--sg-cols, 0) + 1)) / var(--sg-cols, 0));
  --sg-card-height: calc(var(--sg-card-width) / var(--sg-gap));
  --sg-row-unit-2: calc(var(--maxwidth) * 0.03);
  --sg-row-unit-3: calc(var(--maxwidth) * 0.02);
  --sg-row-unit-4: calc(var(--maxwidth) * 0.015);
  --sg-row-unit-5: calc(var(--maxwidth) * 0.012);
  --sg-row-unit-6: calc(var(--maxwidth) * 0.01);

  position: relative;
  transition: opacity calc(var(--sg-opacity-duration, 0) * 1ms) linear calc(var(--sg-opacity-delay, 0) * 1ms);
  display: grid;
  width: 100%;
  box-sizing: border-box;
  grid-template-columns: repeat(var(--sg-cols, 0), 1fr);
  grid-auto-rows: var(--sg-row-unit);
  overflow: hidden;
  padding-inline: var(--sg-gap-margin);
  margin-block-end: calc(40px - var(--sg-gap-margin));

  @supports (animation-timeline: view()) {
    view-timeline-name: --sg-list;

    @keyframes sg-card-move {
      100% {
        transform: translateY(var(--sg-card-offset-computed, 0));
      }
    }

    @keyframes sg-card-media-move {
      0% {
        transform: translateY(var(--sg-card-media-offset-computed, 0));
      }
      100% {
        transform: translateY(0%);
      }
    }
  }

  .sg-straight & {
    --sg-opacity: 1;
  }
`

const Status = styled.p`
  ${visuallyHidden}
`

function getColNumber(v) {
  return (
    2 +
    COL_MAPPING.reduce((a, w, i) => {
      if (v >= w) a = i
      return a
    }, 0)
  )
}

const More = styled.button`
  ${buttonInline}
  ${green}
  /* margin-block-end: 2rem; */
  padding: 0 !important;

  &::after {
    display: block;
    position: relative;
    content: "";
    height: 1px;
    width: calc(var(--maxwidth) - 2 * var(--sg-gap));
    margin-block: 40px 0;
    background: rgb(var(--light-grey));
  }
`

function SGMore({ cards }) {
  const console = useConsole()
  const dictionary = useDictionary()

  const { range, straight } = useSG()

  const [active, setActive] = useState()

  function isActive(v) {
    return cards.length > v * RANGE_INC
  }
  function onRange(v) {
    if (v < 1) return
    setActive(isActive(v))
    window.history.replaceState(
      {},
      "",
      setSelected([
        {
          k: PARAM_KEY_RANGE,
          v: v,
        },
      ])
    )
  }
  useMotionValueEvent(range, "change", onRange)

  useLayoutEffect(() => {
    setActive(isActive(range.get() || 1))
    //    if (range.get() === 0) range.set(1)
    //    else range.set(1)
    //    if (range.get() === 1) setActive(isActive(1))
    //    else range.set(1)
  }, [cards])

  function onClick(e) {
    straight.set(0)
    range.set(range.get() + 1)
  }

  return active ? (
    <More type='button' onClick={onClick}>
      {dictionary.viewMore()}
    </More>
  ) : null
}

function SGList({ items, quotes, categories, filters, mask, rfloading, onOpenFallbackModal }) {
  const console = useConsole()
  const dictionary = useDictionary()

  const { category, avail_category, filter, avail_filter, cols, VH, range, straight } = useSG()
  const { dyna_selection } = useAggregator()

  const rfmain = useRef(null)
  const rfoffsets = useRef([])
  const rfstatus = useRef(null)
  const rftm = useRef(0)

  const H = useMotionValue(0)
  const W = useMotionValue(0)

  const { scrollYProgress: YP } = useScroll({ target: rfmain, offset: ["start start", "end end"] })

  const showStatus = categories.item.length || filters.item.length

  function isCategory(item, v) {
    return v === "all" || item.category?.includes(v)
  }

  function isFilter(item, v) {
    const arr = v.split(FILTER_SEPERATOR)
    const found = item?.filter?.split(FILTER_SEPERATOR).reduce((a, value) => {
      return (a = a || arr.indexOf(value) >= 0)
    }, false)

    return v === "" || found
  }

  const filterItems = useCallback(
    (category, filter) => {
      return items.filter(item => isCategory(item, category) && isFilter(item, filter))
    },
    [items]
  )

  const [cards, setCards] = useState(filterItems(category.get(), filter.get()))

  function getColPosition(el, arr) {
    const l = el?.offsetLeft ?? 0
    return arr?.reduce((a, v, i) => (l >= v ? i : a), 0)
  }

  function get_H_Offsets(arr, count) {
    rfoffsets.current = []
    return arr
      .slice(0, count)
      .filter(Boolean)
      .map(el => el.offsetLeft)
  }

  useLayoutEffect(() => {
    updateColPositions(cols.get())
  }, [cards])

  function set_V_Offsets(count) {
    const offsets = Array.from({ length: count }).map((v, i) => {
      const all = [...rfmain.current?.querySelectorAll(`li[data-col="${i + 1}"]`)] || []
      const el = all.reverse()[0]
      return el?.offsetTop + el?.offsetHeight ?? 0
    })
    const min = offsets.reduce((a, v) => Math.min(a, v), Infinity)
    const active = Math.ceil(cards.length / count) > 4
    setProperty(rfmain.current, "--sg-on", active ? +(min > VH.get()) : 0)

    const max = offsets.reduce((a, v) => Math.max(a, v), 0)

    offsets.forEach((offset, i) => {
      //    setProperty(rfmain.current, `--sg-offset-${i + 1}`, max - offset)
      const value = max - offset
      const options = {
        duration: 0.4 * (1 - straight.get()),
        ease: cubicBezier(0.61, 1, 0.88, 1),
      }
      switch (i) {
        case 0:
          animate(rfmain.current, { "--sg-offset-1": value }, options)
          break
        case 1:
          animate(rfmain.current, { "--sg-offset-2": value }, options)
          break
        case 2:
          animate(rfmain.current, { "--sg-offset-3": value }, options)
          break
        case 3:
          animate(rfmain.current, { "--sg-offset-4": value }, options)
          break
        case 4:
          animate(rfmain.current, { "--sg-offset-5": value }, options)
          break
        case 5:
          animate(rfmain.current, { "--sg-offset-6": value }, options)
          break
        default:
          break
      }
    })
  }

  useLayoutEffect(() => {
    avail_filter.set(getAvailableFilters(category.get()))
    avail_category.set(getAvailableCategories(filter.get()))
  }, [items])

  function updateColPositions(count) {
    const all = [...rfmain.current?.querySelectorAll("li")] || []
    const offsets = get_H_Offsets(all, count)

    all.forEach((el, i) => {
      const col = getColPosition(el, offsets)
      const top = el.offsetTop
      setProperty(el, "--sg-card-top", `${top}px`)
      if (rfoffsets.current?.[i] !== col) {
        setAttribute(el, "data-col", col + 1)
        rfoffsets.current[i] = col
      }
    })
    rafDelay(() => set_V_Offsets(count))
  }

  // remove child event/edito cards from grid
  function removeChidEvents() {
    setCards(cards => {
      return cards.filter(v => !v.parent_element_uid)
    })
  }

  // move roller items after View more page
  function offsetDyna(arr) {
    let selected = dyna_selection.get()?.split(",") || []
    selected = arr?.filter(v => selected.includes(v.slug)) || []
    const count = selected.length
    if (arr.length < RANGE_INC + count) return arr
    while (selected.length) {
      offset(arr, selected.shift().slug)
    }
    return arr

    function offset(arr, slug) {
      const card = arr.find(v => v?.slug === slug)
      if (card) {
        const index = arr.indexOf(card)
        if (index >= 0 && index < RANGE_INC) {
          const l = arr.length - 1
          arr.splice(index, 1)
          arr.splice(Math.min(index + RANGE_INC + count, l), 0, card)
        }
      }
    }
  }

  // insert quotes cards
  const QUOTE_REPEAT = 6

  function insertQuotes(count) {
    const cat = category.get()
    let offset = QUOTE_REPEAT + count
    setCards(cards => {
      let cleaned = cards?.filter(v => !v.quote)
      quotes
        .filter(q => {
          const q_cat = q.taxonomies?.find(v => v.taxonomy_uid === "category")
          return cat === "all" || q_cat?.term_uid === cat
        })
        .forEach((q, i) => {
          let index = (i + 1) * offset + (i % 2)
          if (index < cards.length) cleaned.splice(index, 0, q)
        })
      return offsetDyna(cleaned)
    })
  }

  function onDynaSelection() {
    update(cols.get())
  }
  useMotionValueEvent(dyna_selection, "change", onDynaSelection)

  function update(count) {
    setProperty(rfmain.current, "--sg-cols", count)
    setProperty(rfmain.current, "--sg-row-unit", `var(--sg-row-unit-${count})`)
    removeChidEvents()
    insertQuotes(count)
    rafDelay(() => {
      updateColPositions(count)
      if (!rfloading.current) mask(0)
    })
  }

  function onCols(v) {
    mask(1).then(() => update(v))
  }
  useMotionValueEvent(cols, "change", onCols)

  function onVH(v) {
    set_V_Offsets(cols.get())
  }
  useMotionValueEvent(VH, "change", onVH)

  function onH(v) {
    set_V_Offsets(cols.get())
  }
  useMotionValueEvent(H, "change", onH)

  function onW(v) {
    set_V_Offsets(cols.get())
  }
  useMotionValueEvent(W, "change", onW)

  function onCategory(v) {
    setCards(filterItems(v, filter.get()))
    avail_filter.set(getAvailableFilters(v))
    rafDelay(() => update(cols.get()))
  }
  useMotionValueEvent(category, "change", onCategory)

  function onFilter(v) {
    setCards(filterItems(category.get(), v))
    avail_category.set(getAvailableCategories(v))
    rafDelay(() => update(cols.get()))
  }
  useMotionValueEvent(filter, "change", onFilter)

  function onYP(v) {
    setProperty(rfmain.current, "--sg-y-progress", v.toFixed(5))
  }
  useMotionValueEvent(YP, "change", onYP)

  function onResize({ isize, bsize, target }) {
    clearTimeout(rftm.current)
    rftm.current = setTimeout(resize, 200)

    function resize() {
      H.set(bsize)
      W.set(isize)
    }
  }
  useLayoutEffect(() => observeResize(rfmain.current, onResize), [])

  function getAvailableCategories(v) {
    let all = v.split(FILTER_SEPERATOR).filter(Boolean)
    if (!all.length) all.push("")
    const raw = all
      .reduce((a, v) => {
        const raw = items
          .filter(item => matchFilter(item.filter, v))
          .map(item => item.category)
          .reduce((a, v) => {
            return a.concat(v)
          }, [])
        return a.concat(raw)
      }, [])
      .filter(Boolean)

    return [...new Set(raw.concat("all"))].join(FILTER_SEPERATOR)

    function matchFilter(source, target) {
      if (target === "") return true
      const all = source?.split(FILTER_SEPERATOR).filter(Boolean)
      const index = all?.indexOf(target)
      return +(index >= 0)
    }
  }

  function getAvailableFilters(v) {
    const raw = items
      .filter(item => {
        return v === "all" || item.category?.includes(v)
        // return v === "all" || item.category === v
      })
      .map(item => item.filter)
      .filter(Boolean)
      .reduce((a, v) => {
        return a.concat(v.split(FILTER_SEPERATOR))
      }, [])
      .filter(Boolean)

    return [...new Set(raw)].join(FILTER_SEPERATOR)
  }

  function updateStatus() {
    if (!showStatus) return
    const cat = categories?.item.find(v => (category.get() === "all" ? v.value === "all" : v.term.select === category.get()))
    const filt = filters?.item.find(v => filter.get() === "all" || v.value === filter.get())
    const catStr = cat ? `${cat.value === "all" ? "" : `${cat.label}${filt ? ", " : ""}`}` : ""
    const filtStr = `${filt ? `${filt.label}` : ""}${(cat && cat.value) !== "all" || filt ? ": " : ""}`
    const str = `${catStr}${filtStr}`
    rfstatus.current.innerText = dictionary.gridResultsFiltered(str, cards.length)
  }

  useMotionValueEvent(category, "change", updateStatus)
  useMotionValueEvent(filter, "change", updateStatus)

  useLayoutEffect(() => {
    // if (rfloading.current) return
    updateStatus()
  }, [cards])

  function triggerWa(name, position) {
    if (!process.browser) return

    storeCustomEvent({ eventName: "cardGridClicked" })

    const cat = categories.item.find(v => v.value === category.get())
    const info = {
      cardName: name,
      cardTheme: cat?.wa_name,
      cardPosition: position,
    }
    sessionStorage?.setItem("cardGridInfo", JSON.stringify(info))
  }

  function onRange(v) {
    const all = [...rfmain.current?.querySelectorAll("li")] || []
    const last = all[all.length - 1]
    setTimeout(() => updateColPositions(cols.get()), 100)
    setTimeout(() => {
      last.nextSibling?.getElementsByTagName("a")?.[0]?.focus()
    }, 300)
  }
  useMotionValueEvent(range, "change", onRange)

  return (
    <>
      {showStatus ? <Status ref={rfstatus} role='status' /> : null}
      <List
        ref={rfmain}
        style={{
          "--sg-opacity-duration-out": OPACITY_DURATION_OUT,
          "--sg-opacity-duration-in": OPACITY_DURATION_IN,
        }}
      >
        {cards?.map((card, i) => (
          <Card
            key={`${card.cs_uid || card.uid}-${i}`}
            {...card}
            grid_index={i}
            cols={cols}
            range={range}
            rfloading={rfloading}
            triggerWa={triggerWa}
            onOpenFallbackModal={onOpenFallbackModal}
          />
        ))}
      </List>
      <SGMore cards={cards} />
    </>
  )
}

function SGAnchor({ zekey }) {
  const console = useConsole()

  const rfmain = useRef(null)

  function onUpdate(v) {
    const { top } = rfmain.current?.getBoundingClientRect()
    if (!v || top >= 0) return
    rfmain.current?.scrollIntoView({
      block: "start",
      behavior: "auto",
    })
  }
  useMotionValueEvent(zekey, "change", onUpdate)

  return <Anchor ref={rfmain} aria-hidden='true' />
}

function buildAsset(o) {
  const {
    public_id_image_0,
    public_id_video_0,
    public_image_0_width,
    public_image_0_height,
    public_id_image_1,
    public_id_video_1,
    public_image_1_width,
    public_image_1_height,
    public_id_image_2,
    public_id_video_2,
    public_image_2_width,
    public_image_2_height,
  } = o

  return [
    {
      resource_type: "image",
      public_id: public_id_image_0,
      width: public_image_0_width,
      height: public_image_0_height,
    },
    {
      resource_type: "image",
      public_id: public_id_image_1,
      width: public_image_1_width,
      height: public_image_1_height,
    },
    {
      resource_type: "image",
      public_id: public_id_image_2,
      width: public_image_2_width,
      height: public_image_2_height,
    },
    {
      resource_type: "video",
      public_id: public_id_video_0,
      width: public_image_0_width,
      height: public_image_0_height,
    },
    {
      resource_type: "video",
      public_id: public_id_video_1,
      width: public_image_1_width,
      height: public_image_1_height,
    },
    {
      resource_type: "video",
      public_id: public_id_video_2,
      width: public_image_2_width,
      height: public_image_2_height,
    },
  ].filter(v => v.public_id)
}

export function buildHref(o) {
  //  console.log("buildHref", o)
  const { documenttype, aggregator, slug, discipline, link_href, parent_slug } = o
  //  if (!slug || !taxonomies?.length) return null
  //  const discipline = taxonomies?.find(v => v.taxonomy_uid === "discipline")

  switch (documenttype) {
    case "event":
      //      if (!discipline) return null
      return `/${aggregatorPathByTerm[aggregator]}/${discipline}/${parent_slug ? `${parent_slug}/` : ""}${slug}`
    case "edito":
      //      if (!discipline) return null
      return link_href
    case "testimonee":
      //      if (!discipline) return null
      return `/rolex-family/${discipline}/${slug}`
    default:
      return null
  }
}

function ScrollingGrid({ spacing, background_color, card, categories, filters, quotes = [] }) {
  const console = useConsole()
  const dictionary = useDictionary()
  const locale = useLocale()
  const location = useLocation()
  const navigation = useNavigation()
  const ssr = useSSR()
  const nav = useNav()
  const env = useEnv()
  const [isFallbackModalOpen, setIsFallbackModalOpen] = useState(false)
  const [modalLanguages, setModalLanguages] = useState([])
  const [fallbackLink, setFallbackLink] = useState("")
  const items = card?.map((item, i) => ({
    ...item,
    index: i,
    asset: buildAsset(item),
    href: buildHref(item),
    languages: normalizeLanguagesTaxonomy(item.languages_taxonomy),
  }))

  const { cols, category, filter, VH, T, range, straight } = useSG()

  const rfmain = useRef(null)
  const avail_priority = useRef(null)
  const rfvh = useRef(null)
  const rfloading = useRef(1)
  const rftm = useRef(0)

  const { scrollY: Y } = useScroll({ target: rfmain, offset: ["start end", "end end"] })

  function onResize({ isize, target }) {
    cols.set(getColNumber(isize))
    T.set(target.offsetTop + Math.ceil(nav.main.safe.motion.height.get() - 1))
  }
  useLayoutEffect(() => observeResize(rfmain.current, onResize), [])

  function onResizeVh({ bsize }) {
    clearTimeout(rftm.current)
    rftm.current = setTimeout(resize, 200)

    function resize() {
      VH.set(bsize)
    }
  }
  useLayoutEffect(() => observeResize(rfvh.current, onResizeVh), [])

  function getBackground(gradient) {
    let ret = "transparent"
    if (!gradient) return ret
    const colors = gradient.colors ? gradient.colors.map(({ color, opacity }) => `rgb(${color} / ${opacity})`) : []
    ret = colors.length ? `linear-gradient(${gradient.angle ? `${gradient.angle}deg, ` : ""}${colors})` : ret
    return ret
  }

  function onStraight(v) {
    rfmain.current.classList[v ? "add" : "remove"]("sg-straight")
  }
  useMotionValueEvent(straight, "change", onStraight)

  useLayoutEffect(() => {
    category.set(getSelected(PARAM_KEY_CATEGORY, "all"))
    filter.set(getSelected(PARAM_KEY_FILTER, "all"))
    range.set(+getSelected(PARAM_KEY_RANGE, 0))
    if (range.get() === 0) {
      straight.set(0)
      range.set(1)
    } else {
      straight.set(1)
    }
  }, [location])

  function mask(state) {
    return new Promise(resolve => {
      setProperty(rfmain.current, "--sg-opacity", 1 - state)
      if (!state) {
        return resolve()
      }

      if (!rfloading.current) {
        animate(nav.main.bar.motion.pinned, 1, {
          duration: OPACITY_DURATION_OUT / 1000,
        })
      }
      setTimeout(resolve, rfloading.current ? 0 : OPACITY_DURATION_OUT)
    })
  }

  function mayReset(v, priority) {
    switch (priority) {
      case "filter":
        return includesCategory(v)
      case "category":
        return includesFilter(v)
      default:
        return null
    }
  }

  function findFilter(comp, target) {
    const all = target?.split(FILTER_SEPERATOR).filter(Boolean)
    const index = all?.indexOf(comp)
    return index >= 0
  }

  function includesCategory(v) {
    const target = category.get()
    const all = v?.split(FILTER_SEPERATOR).filter(Boolean)
    const match = all.reduce((a, v) => {
      const state = items.find(item => (target === "all" || item.category?.includes(target)) && findFilter(v, item.filter))
      return a || !!state
    }, false)

    return match ? target : "all"
  }

  function includesFilter(v) {
    const target = filter.get()
    const targets = target?.split(FILTER_SEPERATOR).filter(Boolean)
    const match = targets.reduce((a, t) => {
      const state = items.find(item => (target === "" || findFilter(t, item.filter)) && (v === "all" || item.category?.includes(v)))
      return a || !!state
    }, false)

    return match ? target : ""
  }

  function updateKey() {
    if (rfloading.current) return null
    return `${category.get()}_${filter.get()}`
  }
  const zekey = useTransform([category, filter], () => updateKey()) //

  const [key, setKey] = useState(null)
  function onKey(v) {
    if (!v) return
    setKey(v)
  }
  useMotionValueEvent(zekey, "change", onKey)

  useLayoutEffect(() => {
    const el = rfmain.current

    const observer = new IntersectionObserver(([{ isIntersecting }]) => {
      if (isIntersecting) {
        observer.unobserve(el)
        if (rfloading.current) {
          setProperty(rfmain.current, "--sg-opacity", rfloading.current)
          rfloading.current = 0
        }
      }
    })
    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [key])

  const { margin_top, margin_bottom, padding_bottom } = spacing ?? {}

  const cn = [margin_top, margin_bottom].filter(Boolean).join(" ")

  const grid_cn = [padding_bottom].filter(Boolean).join(" ")

  function normalizeLanguagesTaxonomy(value) {
    return Array.isArray(value)
      ? value
          .flatMap(lang => String(lang).split(";"))
          .map(lang => lang.trim().toLowerCase())
          .filter(Boolean)
      : value
      ? String(value)
          .split(";")
          .map(lang => lang.trim().toLowerCase())
          .filter(Boolean)
      : []
  }

  const onOpenFallbackModal = item => {
    setModalLanguages(item.languages || [])
    setFallbackLink(item.href || "")
    setIsFallbackModalOpen(true)
  }

  const onCloseFallbackModal = () => {
    setIsFallbackModalOpen(false)
    setModalLanguages([])
    setFallbackLink("")
  }

  return (
    <>
      <Main
        ref={rfmain}
        style={{
          "--sg-opacity": 1,
          "--sg-background": getBackground(background_color),
          "--sg-cols": cols.get(),
          "--sg-row-unit": `var(--sg-row-unit-${cols.get()})`,
        }}
        className={cn}
      >
        <Background ref={rfvh} />
        <Grid className={grid_cn}>
          <SGAnchor zekey={zekey} />
          <Menu categories={categories} filters={filters} avail_priority={avail_priority} mayReset={mayReset} mask={mask} Y={Y} />
          <SGList
            items={items}
            quotes={quotes}
            categories={categories}
            filters={filters}
            rfloading={rfloading}
            mask={mask}
            onOpenFallbackModal={onOpenFallbackModal}
          />
        </Grid>
      </Main>

      {!ssr &&
        createPortal(
          <AnimatePresence>
            {isFallbackModalOpen && (
              <ModalContainer onClose={onCloseFallbackModal} className={fallbackModal} modalHeading='modal-fallback-org'>
                <div className='fallbackLanguages'>
                  <h2>{dictionary.fallbackModalTitle()}</h2>

                  <p>{dictionary.fallbackModalDescription()}</p>

                  <ul className='fallbackList'>
                    {modalLanguages.map(langCode => {
                      const matchingLocale = locale?.locales?.find(loc => {
                        const www = loc.codes?.www?.toLowerCase()
                        const ws = loc.codes?.ws?.toLowerCase()

                        return www === langCode || ws === langCode || www?.split("-")[0] === langCode || ws?.split("-")[0] === langCode
                      })

                      if (!matchingLocale) return null

                      const targetLang = matchingLocale.codes?.www || langCode

                      let href = navigation?.localize(fallbackLink, targetLang)

                      if (env?.cn && matchingLocale.external) {
                        href = new URL(href, "https://rolex.com").href
                      } else if (!env?.cn && targetLang === "zh-hans-cn") {
                        href = new URL(fallbackLink, "https://www.rolex.cn").href
                      }

                      return (
                        <li key={langCode}>
                          <Link className='inline' icon='none' href={href} target='_self' rel='noopener'>
                            {matchingLocale.label || langCode}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </ModalContainer>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  )
}

export default function SG(props) {
  return (
    <SGProvider>
      <ScrollingGrid {...props} />
    </SGProvider>
  )
}
